import { useState, useRef, useEffect } from "react";

const TMDB_KEY = "cb8760ca6129553d4db9cab860331084";
const TMDB_BASE = "https://api.themoviedb.org/3";
const TMDB_IMG = "https://image.tmdb.org/t/p/w200";
const JELLYFIN_URL = "https://jellyfin.johancarlson.com";
const JELLYFIN_KEY = "218472f7d8ac40c894330a0d9662daa1";

function normalize(str) {
  return str.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function useLocalStorage(key, initial) {
  const [val, setVal] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key)) ?? initial; }
    catch { return initial; }
  });
  useEffect(() => { localStorage.setItem(key, JSON.stringify(val)); }, [key, val]);
  return [val, setVal];
}

// Compress image to max 1024px wide, quality 0.7
function compressImage(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1024;
        const scale = img.width > maxW ? maxW / img.width : 1;
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL("image/jpeg", 0.7);
        resolve(compressed);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const TABS = ["library", "scan"];

export default function App() {
  const [tab, setTab] = useState("library");
  const [library, setLibrary] = useLocalStorage("discvault_library", []);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const [addModal, setAddModal] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);

  const [scanResults, setScanResults] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const fileRef = useRef();

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const filtered = library.filter((item) => {
    const matchSearch = item.title.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || item.type === filter;
    return matchSearch && matchFilter;
  });

  async function searchTMDB(query) {
    if (!query.trim()) return;
    setAddSearching(true);
    setAddResults([]);
    try {
      const [mRes, tRes] = await Promise.all([
        fetch(`${TMDB_BASE}/search/movie?api_key=${TMDB_KEY}&query=${encodeURIComponent(query)}`),
        fetch(`${TMDB_BASE}/search/tv?api_key=${TMDB_KEY}&query=${encodeURIComponent(query)}`),
      ]);
      const [mData, tData] = await Promise.all([mRes.json(), tRes.json()]);
      const movies = (mData.results || []).slice(0, 4).map((r) => ({
        tmdb_id: r.id, title: r.title, type: "movie",
        year: r.release_date?.slice(0, 4) || null,
        poster: r.poster_path ? TMDB_IMG + r.poster_path : null,
        overview: r.overview,
      }));
      const shows = (tData.results || []).slice(0, 4).map((r) => ({
        tmdb_id: r.id, title: r.name, type: "tv",
        year: r.first_air_date?.slice(0, 4) || null,
        poster: r.poster_path ? TMDB_IMG + r.poster_path : null,
        overview: r.overview,
      }));
      setAddResults([...movies, ...shows]);
    } catch {
      setAddResults([]);
    } finally {
      setAddSearching(false);
    }
  }

  function addItem(item) {
    if (!library.some((i) => i.tmdb_id === item.tmdb_id && i.type === item.type)) {
      setLibrary((prev) => [{ id: Date.now(), ...item, wishlist: false }, ...prev]);
    }
    setAddModal(false);
    setAddQuery("");
    setAddResults([]);
  }

  function addToWishlist(item) {
    if (!library.some((i) => normalize(i.title) === normalize(item.title))) {
      setLibrary((prev) => [{ id: Date.now(), ...item, wishlist: true }, ...prev]);
    }
  }

  function removeItem(id) {
    setLibrary((prev) => prev.filter((i) => i.id !== id));
  }

  function checkOwnership(title) {
    const norm = normalize(title);
    return library.some(
      (item) => !item.wishlist && (normalize(item.title).includes(norm) || norm.includes(normalize(item.title)))
    );
  }

  async function syncJellyfin() {
    setSyncing(true); setSyncMsg("");
    try {
      const res = await fetch(
        `${JELLYFIN_URL}/Items?IncludeItemTypes=Movie,Series&Recursive=true&api_key=${JELLYFIN_KEY}`
      );
      if (!res.ok) throw new Error("Auth failed");
      const data = await res.json();
      const items = (data.Items || []).map((item) => ({
        id: Date.now() + Math.random(),
        tmdb_id: null,
        title: item.Name,
        type: item.Type === "Movie" ? "movie" : "tv",
        year: item.ProductionYear || null,
        poster: item.ImageTags?.Primary
          ? `${JELLYFIN_URL}/Items/${item.Id}/Images/Primary?api_key=${JELLYFIN_KEY}&maxHeight=200`
          : null,
        overview: item.Overview || "",
        wishlist: false,
        fromJellyfin: true,
      }));
      setLibrary((prev) => {
        const manual = prev.filter((i) => !i.fromJellyfin);
        return [...manual, ...items];
      });
      setSyncMsg(`Synced ${items.length} titles from Jellyfin.`);
    } catch {
      setSyncMsg("Could not connect to Jellyfin. Check server status.");
    } finally {
      setSyncing(false);
    }
  }

  async function handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setScanResults([]); setScanError(""); setScanning(true);
    try {
      const compressed = await compressImage(file);
      setImagePreview(compressed);
      const base64 = compressed.split(",")[1];

      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{
            role: "user",
            content: [
              { type: "image", source: { type: "base64", media_type: "image/jpeg", data: base64 } },
              { type: "text", text: `Read every DVD or Blu-ray title visible in this image. Return ONLY a JSON array of strings. No markdown, no explanation. Example: ["The Matrix","Friends Season 2"]` },
            ],
          }],
        }),
      });

      const data = await response.json();
      const text = data.content?.find((b) => b.type === "text")?.text || "[]";
      let titles;
      try { titles = JSON.parse(text.replace(/```json|```/g, "").trim()); }
      catch { titles = []; }

      const results = await Promise.all(titles.map(async (title) => {
        const owned = checkOwnership(title);
        let poster = null;
        try {
          const r = await fetch(`${TMDB_BASE}/search/multi?api_key=${TMDB_KEY}&query=${encodeURIComponent(title)}`);
          const d = await r.json();
          const top = d.results?.[0];
          if (top?.poster_path) poster = TMDB_IMG + top.poster_path;
        } catch {}
        return { title, owned, poster };
      }));
      setScanResults(results);
    } catch {
      setScanError("Could not analyze image. Please try again.");
    } finally {
      setScanning(false);
    }
  }

  const s = {
    root: { minHeight: "100vh", background: "#0a0a0a", color: "#f0ede6", fontFamily: "system-ui, sans-serif", maxWidth: 480, margin: "0 auto" },
    header: { padding: "20px 16px 0", borderBottom: "1px solid #161616" },
    wordmark: { display: "flex", alignItems: "baseline", gap: 6, marginBottom: 14 },
    tabBar: { display: "flex" },
    tab: (active) => ({
      flex: 1, padding: "10px 0", background: "none", border: "none",
      borderBottom: active ? "2px solid #e63946" : "2px solid transparent",
      color: active ? "#f0ede6" : "#444", fontFamily: "inherit",
      fontSize: 11, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
    }),
    body: { padding: 16 },
    input: { background: "#111", border: "1px solid #1e1e1e", color: "#f0ede6", padding: "10px 14px", fontFamily: "inherit", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" },
    card: { background: "#111", border: "1px solid #1a1a1a", display: "flex", gap: 12, padding: 12, marginBottom: 6 },
    btnGhost: { background: "none", border: "1px solid #222", color: "#888", fontFamily: "inherit", fontSize: 11, padding: "9px 14px", cursor: "pointer", letterSpacing: 1 },
    btnSolid: { background: "#f0ede6", border: "none", color: "#0a0a0a", fontFamily: "inherit", fontSize: 11, fontWeight: 700, padding: "10px 18px", cursor: "pointer", letterSpacing: 2 },
    tag: (color) => ({ display: "inline-block", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "2px 7px", border: `1px solid ${color}44`, color: color }),
  };

  return (
    <div style={s.root}>
      <div style={s.header}>
        <div style={s.wordmark}>
          <span style={{ fontSize: 30, fontWeight: 900, letterSpacing: 1, color: "#fff" }}>DISC</span>
          <span style={{ fontSize: 30, fontWeight: 900, letterSpacing: 1, color: "#e63946" }}>VAULT</span>
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#333", letterSpacing: 2 }}>{library.filter(i => !i.wishlist).length} OWNED</span>
        </div>
        <div style={s.tabBar}>
          {TABS.map((t) => (
            <button key={t} style={s.tab(tab === t)} onClick={() => setTab(t)}>
              {t === "library" ? "▤ Library" : "⊙ Scan"}
            </button>
          ))}
        </div>
      </div>

      {/* Library */}
      {tab === "library" && (
        <div style={s.body}>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input style={{ ...s.input, flex: 1 }} placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ ...s.input, width: 110 }}>
              <option value="all">All</option>
              <option value="movie">Movies</option>
              <option value="tv">TV Shows</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <button style={{ ...s.btnGhost, flex: 1 }} onClick={() => setAddModal(true)}>+ ADD TITLE</button>
            <button style={{ ...s.btnGhost, flex: 1 }} onClick={syncJellyfin} disabled={syncing}>
              {syncing ? "SYNCING..." : "⟳ JELLYFIN"}
            </button>
          </div>
          {syncMsg && (
            <div style={{ fontSize: 12, color: syncMsg.startsWith("Synced") ? "#4ade80" : "#f87171", marginBottom: 12, padding: "8px 12px", background: "#111", border: "1px solid #1e1e1e" }}>
              {syncMsg}
            </div>
          )}
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", color: "#333", padding: 48, fontSize: 13 }}>
              {library.length === 0 ? "Vault is empty. Add titles or sync Jellyfin." : "No results."}
            </div>
          )}
          {filtered.map((item) => (
            <div key={item.id} style={{ ...s.card, borderLeft: `3px solid ${item.wishlist ? "#fbbf24" : item.fromJellyfin ? "#e63946" : "#1a1a1a"}` }}>
              {item.poster
                ? <img src={item.poster} alt="" style={{ width: 44, height: 66, objectFit: "cover", flexShrink: 0 }} />
                : <div style={{ width: 44, height: 66, background: "#1a1a1a", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{item.type === "movie" ? "🎬" : "📺"}</div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</div>
                <div style={{ fontSize: 11, color: "#555", marginTop: 3 }}>{item.year || "—"} · {item.type === "movie" ? "Movie" : "TV"}</div>
                <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
                  {item.wishlist && <span style={s.tag("#fbbf24")}>⭐ Wishlist</span>}
                  {item.fromJellyfin && <span style={s.tag("#e63946")}>Jellyfin</span>}
                </div>
              </div>
              <button onClick={() => removeItem(item.id)} style={{ background: "none", border: "none", color: "#333", cursor: "pointer", fontSize: 20, alignSelf: "flex-start", lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* Scan */}
      {tab === "scan" && (
        <div style={s.body}>
          <p style={{ fontSize: 12, color: "#555", lineHeight: 1.8, marginBottom: 14 }}>
            Photo a DVD rack. AI reads the titles and checks your vault.
          </p>
          <div onClick={() => fileRef.current?.click()} style={{ border: "1px dashed #222", padding: 32, textAlign: "center", cursor: "pointer", background: "#111", marginBottom: 14 }}>
            {imagePreview
              ? <img src={imagePreview} alt="" style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain" }} />
              : <><div style={{ fontSize: 38, marginBottom: 8 }}>📷</div><div style={{ fontSize: 11, color: "#444", letterSpacing: 3 }}>TAP TO PHOTOGRAPH</div></>
            }
          </div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handleImageUpload} style={{ display: "none" }} />
          {scanning && <div style={{ textAlign: "center", color: "#555", fontSize: 11, letterSpacing: 3, padding: 20 }}>READING TITLES...</div>}
          {scanError && <div style={{ color: "#f87171", fontSize: 12, padding: 12, background: "#1a0000", border: "1px solid #330000", marginBottom: 12 }}>{scanError}</div>}
          {scanResults.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: "#444", letterSpacing: 3, marginBottom: 10 }}>{scanResults.length} TITLES FOUND</div>
              {scanResults.map((item, i) => (
                <div key={i} style={{ ...s.card, alignItems: "center", borderLeft: `3px solid ${item.owned ? "#4ade80" : "#1a1a1a"}` }}>
                  {item.poster
                    ? <img src={item.poster} alt="" style={{ width: 36, height: 54, objectFit: "cover", flexShrink: 0 }} />
                    : <div style={{ width: 36, height: 54, background: "#1a1a1a", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{item.owned ? "✅" : "📀"}</div>
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</div>
                    <div style={{ fontSize: 11, marginTop: 3, color: item.owned ? "#4ade80" : "#555" }}>{item.owned ? "In your vault" : "Not owned"}</div>
                  </div>
                  {!item.owned && (
                    <button style={{ ...s.btnGhost, fontSize: 10, color: "#fbbf24", borderColor: "#fbbf2433", whiteSpace: "nowrap" }}
                      onClick={() => addToWishlist({ title: item.title, poster: item.poster, type: "movie", year: null })}>
                      ⭐ WISH
                    </button>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Add Modal */}
      {addModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "flex-end", zIndex: 100 }}>
          <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", background: "#0f0f0f", padding: 16, borderTop: "1px solid #1e1e1e", maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ fontSize: 11, letterSpacing: 3, color: "#444", marginBottom: 12 }}>ADD TO VAULT</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input style={{ ...s.input, flex: 1 }} placeholder="Search movies or TV shows..." value={addQuery}
                onChange={(e) => setAddQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchTMDB(addQuery)} autoFocus />
              <button style={s.btnSolid} onClick={() => searchTMDB(addQuery)}>GO</button>
            </div>
            {addSearching && <div style={{ textAlign: "center", color: "#444", fontSize: 11, letterSpacing: 3, padding: 16 }}>SEARCHING...</div>}
            {addResults.map((item, i) => (
              <div key={i} onClick={() => addItem(item)} style={{ ...s.card, cursor: "pointer", marginBottom: 6 }}>
                {item.poster
                  ? <img src={item.poster} alt="" style={{ width: 40, height: 60, objectFit: "cover", flexShrink: 0 }} />
                  : <div style={{ width: 40, height: 60, background: "#1a1a1a", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{item.type === "movie" ? "🎬" : "📺"}</div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{item.title}</div>
                  <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{item.year || "—"} · {item.type === "movie" ? "Movie" : "TV"}</div>
                  {item.overview && <div style={{ fontSize: 11, color: "#444", marginTop: 4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.overview}</div>}
                </div>
              </div>
            ))}
            <button style={{ ...s.btnGhost, width: "100%", marginTop: 12 }} onClick={() => { setAddModal(false); setAddQuery(""); setAddResults([]); }}>CANCEL</button>
          </div>
        </div>
      )}
    </div>
  );
}
